import { NextSeo, type NextSeoProps } from 'next-seo';
import { memo, useMemo } from 'react';

import { useGetNotificationSummary } from '~/components/AirNotifications/hooks/useGetNotificationSummary';
import { DocumentTitle } from '~/components/v2/components/shared/seo/DocumentTitle';
import { getAirTabTitle } from '~/components/v2/components/shared/seo/getAirTabTitle';
import { getPrivatePageDocumentTitle } from '~/components/v2/components/shared/seo/getPrivatePageDocumentTitle';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type PrivatePageSeoTitleProps = Omit<NextSeoProps, 'title' | 'defaultTitle' | 'titleTemplate'> & {
  title?: string;
  fallbackToWorkspace?: boolean;
  /** Only applies when no page title is registered, so a late workspace load cannot replace a page title. */
  isFallbackTitle?: boolean;
  /** The title is still loading. Shows a generic title instead of the workspace fallback until it arrives. */
  isTitlePending?: boolean;
};

export const PrivatePageSeoTitle = memo(
  ({
    title,
    fallbackToWorkspace = true,
    isFallbackTitle = false,
    isTitlePending = false,
    ...restOfProps
  }: PrivatePageSeoTitleProps) => {
    const { data: notificationsSummary } = useGetNotificationSummary();
    const { currentWorkspace } = useCurrentWorkspace();

    const formattedTitle = useMemo(
      () =>
        getPrivatePageDocumentTitle({
          pageTitle: title,
          unreadCount: notificationsSummary?.unseen ?? 0,
          workspaceName: fallbackToWorkspace && !isTitlePending ? currentWorkspace?.name : undefined,
        }),
      [currentWorkspace?.name, fallbackToWorkspace, isTitlePending, notificationsSummary?.unseen, title],
    );

    return (
      <>
        {/* The layout fallback stays registered while the workspace loads so the tab is never blank. */}
        <DocumentTitle
          title={getAirTabTitle(formattedTitle)}
          isFallback={isFallbackTitle}
          isPending={isTitlePending || isFallbackTitle}
        />
        <NextSeo {...restOfProps} />
      </>
    );
  },
);

PrivatePageSeoTitle.displayName = 'PrivatePageSeoTitle';
